import { SiteAvailabilityByDayTimeSlot } from '@fingertip/creator-proto/gen/fingertip/creator/nylas/v1/nylas_pb'
import {
  CalendarRemove4Icon,
  MoonIcon,
  SunIcon,
  SunriseIcon,
} from '@fingertip/icons'
import { isEqual, isToday as isTodayFns } from 'date-fns'
import { format, toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { BookingSelection } from '@/lib/hooks/use-booking-selection'
import { RootStoreContext } from '@/lib/stores/root-store'
import { cn } from '@/lib/utils/cn'

import { ButtonDiv } from '../../../ui/button'
import { Skeleton } from '../../../ui/skeleton'
import { Spinner } from '../../../ui/spinner'
import { nameOfDay } from '../../utils'

type AvailableTimesProps = {
  bookingSelection: BookingSelection
  date: Date
  availabilitySlots?: SiteAvailabilityByDayTimeSlot[]
  onTimeslotSelect: (slot: SiteAvailabilityByDayTimeSlot) => void
  seatsPerTimeSlot?: number | null
  showAvailableSeatsCount?: boolean | null
  showTimeFormatToggle?: boolean
  className?: string
  availableMonth?: string | undefined
  isLoading?: boolean
}

export const BookerAvailableTimes = observer(
  ({
    bookingSelection,
    date,
    onTimeslotSelect,
    showTimeFormatToggle = true,
    className,
    availableMonth,
    availabilitySlots,
    isLoading,
  }: AvailableTimesProps) => {
    const { t } = useTranslation()
    const {
      bookerStore: { selectedTimeslot, timeZone },
    } = useContext(RootStoreContext)

    // Convert display date to user's timezone for UI
    const localDate = toZonedTime(date, timeZone)
    const isToday = isTodayFns(localDate)

    return (
      <div className={cn('text-default flex flex-col', className)}>
        <header className="mb-3 flex w-full flex-row items-center font-medium">
          <span className="w-full text-center text-xs text-muted-foreground">
            <span className={cn(isToday && 'text-default!')}>
              {nameOfDay(date, 'long')}
            </span>
            <span className="inline-flex items-center justify-center rounded-3xl px-1 pt-0.5 font-medium">
              {format(date, 'do')}
              {availableMonth && ` ${availableMonth}`}
            </span>
          </span>
        </header>

        <div className="h-full pb-4">
          {!isLoading && !availabilitySlots?.length && (
            <div className="flex h-full flex-col items-center rounded-md border border-muted bg-muted p-6 dark:bg-transparent">
              <CalendarRemove4Icon className="mb-2 size-4 text-muted-foreground" />
              <p
                className={cn(
                  'text-muted-foreground',
                  showTimeFormatToggle ? '-mt-1 text-lg' : 'text-sm',
                )}
              >
                {t('all_booked_today')}
              </p>
            </div>
          )}

          {isLoading && (
            <div className="flex h-full flex-col items-center rounded-md border border-muted bg-muted p-6 dark:bg-transparent">
              <Spinner />
            </div>
          )}

          {/* Group slots by time of day: Morning, Afternoon, Evening */}
          {(() => {
            // Sort all slots by time
            const sortedSlots = (availabilitySlots || []).sort((a, b) => {
              if (!a.time || !b.time) return 0
              // Sort using UTC times
              return a.time.toDate().getTime() - b.time.toDate().getTime()
            })

            // Group slots by time of day
            const morningSlots: SiteAvailabilityByDayTimeSlot[] = []
            const afternoonSlots: SiteAvailabilityByDayTimeSlot[] = []
            const eveningSlots: SiteAvailabilityByDayTimeSlot[] = []

            sortedSlots.forEach((slot) => {
              const slotTime = slot.time?.toDate()
              if (!slotTime) return

              const localSlotTime = toZonedTime(slotTime, timeZone)
              const hours = localSlotTime.getHours()

              if (hours < 12) {
                morningSlots.push(slot)
              } else if (hours < 18) {
                afternoonSlots.push(slot)
              } else {
                eveningSlots.push(slot)
              }
            })

            // Render function for a slot
            const renderSlot = (
              slot: SiteAvailabilityByDayTimeSlot,
              i: number,
            ) => {
              // Get UTC time from slot
              const slotTime = slot.time?.toDate()
              if (!slotTime) return null

              // Compare UTC times for selection
              const selected = !!(
                selectedTimeslot &&
                slotTime &&
                isEqual(selectedTimeslot, slotTime)
              )

              const isFull = slot.groupClass
                ? (slot.groupClass?.available || 0) <= 0
                : false
              const willWaitlist =
                isFull && slot.groupClass && slot.groupClass.waitlistEnabled
              const infoText = isFull
                ? '(Event full)'
                : slot.groupClass &&
                    bookingSelection.bookingItemsShowAttendeeCount
                  ? `(${slot.groupClass.max - slot.groupClass.available}/${slot.groupClass.max} spots taken)`
                  : undefined

              // Convert to local time for display only
              const localSlotTime = toZonedTime(slotTime, timeZone)

              return (
                <ButtonDiv
                  key={i}
                  data-testid="time"
                  data-time={slotTime.toISOString()} // Store UTC time in data attribute
                  onClick={() => onTimeslotSelect(slot)}
                  disabled={
                    slot.groupClass &&
                    !(
                      slot.groupClass.available > 0 ||
                      slot.groupClass?.waitlistEnabled
                    )
                  }
                  className={cn(
                    'mb-1.5 h-auto min-h-[36px] w-full flex flex-row items-center py-2 font-normal gap-2',
                    {
                      'bg-muted text-primary font-bold': selected,
                    },
                  )}
                  variant="secondary"
                  type="button"
                >
                  <div>{format(localSlotTime, 'h:mm aaa')}</div>
                  {!!infoText && (
                    <div className="text-right text-sm text-muted-foreground">
                      {infoText}
                    </div>
                  )}
                  {willWaitlist && (
                    <div>
                      <Badge variant="yellow">Waitlist</Badge>
                    </div>
                  )}
                </ButtonDiv>
              )
            }

            // Render each time category with its slots
            return (
              <>
                {morningSlots.length > 0 && (
                  <div className="mb-4 mt-2">
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground flex justify-center items-center gap-1">
                      <SunriseIcon className="size-5" />
                      <span>Morning</span>
                    </h3>
                    {morningSlots.map((slot, i) => renderSlot(slot, i))}
                  </div>
                )}

                {afternoonSlots.length > 0 && (
                  <div className="mb-4 mt-2">
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground flex justify-center items-center gap-1">
                      <SunIcon className="size-5" />
                      <span>Afternoon</span>
                    </h3>
                    {afternoonSlots.map((slot, i) => renderSlot(slot, i))}
                  </div>
                )}

                {eveningSlots.length > 0 && (
                  <div className="mb-4 mt-2">
                    <h3 className="mb-2 text-sm font-medium text-muted-foreground flex justify-center items-center gap-1">
                      <MoonIcon className="size-5" />
                      <span>Evening</span>
                    </h3>
                    {eveningSlots.map((slot, i) => renderSlot(slot, i))}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      </div>
    )
  },
)

export const AvailableTimesSkeleton = () => (
  <div className="mt-8 flex w-1/5 flex-col only:w-full">
    {/* Random number of elements between 1 and 6. */}
    {Array.from({ length: Math.floor(Math.random() * 6) + 1 }).map((_, i) => (
      <Skeleton className="mb-4 h-6 w-full" key={i} />
    ))}
  </div>
)
